import React, { useState, useEffect } from 'react';

// Environment configuration
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || 'https://api.getdecisionos.com';
};

const ENV_CONFIG = {
  API_URL: getApiUrl()
};

function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [dataCounts, setDataCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    try {
      setLoading(true);
      const response = await fetch(`${ENV_CONFIG.API_URL}/api/user/profile`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      setProfile(data.profile);
      setDataCounts(data.dataCounts);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showDeleteModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showDeleteModal]);

  async function handleDeleteAccount() {
    if (!confirmChecked) {
      return;
    }

    try {
      setDeleting(true);
      const response = await fetch(`${ENV_CONFIG.API_URL}/api/user/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          confirmation: 'DELETE'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete account');
      }

      // Account deleted successfully - clear session and redirect with message
      sessionStorage.setItem('accountDeleted', 'true');
      window.location.href = '/login';
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(err.message);
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Profile</h1>
        </div>
        <div className="profile-loading">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="page">
        <div className="page-header">
          <h1>Profile</h1>
        </div>
        <div className="profile-error">
          <p>{error}</p>
          <button onClick={loadProfile}>Retry</button>
        </div>
      </div>
    );
  }

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <div className="page">
      <div className="profile-content">
        {/* Identity Header */}
        <div className="profile-identity-header">
          <div className="profile-avatar">
            {getInitials(profile.name)}
          </div>
          <div className="profile-identity-text">
            <h1 className="profile-name">{profile.name || 'User'}</h1>
            <p className="profile-email">{profile.email || '—'}</p>
          </div>
        </div>

        {/* Account Information Card */}
        <div className="profile-card">
          <div className="profile-info-item">
            <label>Name</label>
            <span>{profile.name || '—'}</span>
          </div>
          <div className="profile-info-item">
            <label>Email</label>
            <span>{profile.email || '—'}</span>
          </div>
          <div className="profile-info-item">
            <label>Slack ID</label>
            <span className="profile-slack-id-pill">{profile.slackId || '—'}</span>
          </div>
          <div className="profile-info-item">
            <label>Time zone</label>
            <span>{profile.timezone || '—'}</span>
          </div>
          <div className="profile-info-item">
            <label>Member since</label>
            <span>
              {profile.memberSince 
                ? new Date(profile.memberSince).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })
                : '—'}
            </span>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="profile-danger-zone">
          <label className="profile-danger-label">Danger zone</label>
          <p className="profile-danger-description">
            Permanently delete your account and all associated data
          </p>
          <button 
            className="profile-delete-button"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete account
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div 
          className="delete-modal-overlay" 
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div 
            className="delete-modal-container" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="delete-modal-header">
              <h2>Delete account</h2>
              <button 
                className="delete-modal-close"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="delete-modal-body">
              {/* Warning text */}
              <p className="delete-modal-warning">
                This action cannot be undone. Your account and all associated data will be permanently deleted.
              </p>

              {/* Stats panel */}
              {dataCounts && (
                <div className="delete-modal-stats">
                  <div className="delete-stat-item">
                    <div className="delete-stat-number">{dataCounts.objectives}</div>
                    <div className="delete-stat-label">OBJECTIVES</div>
                  </div>
                  <div className="delete-stat-item">
                    <div className="delete-stat-number">{dataCounts.keyResults}</div>
                    <div className="delete-stat-label">KEY RESULTS</div>
                  </div>
                  <div className="delete-stat-item">
                    <div className="delete-stat-number">{dataCounts.captures}</div>
                    <div className="delete-stat-label">CAPTURES</div>
                  </div>
                </div>
              )}

              {/* Checkbox confirmation */}
              <label className="delete-modal-checkbox-row">
                <input
                  type="checkbox"
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  disabled={deleting}
                />
                <span>I understand this is permanent</span>
              </label>

              {/* Error message */}
              {error && (
                <div className="delete-modal-error">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="delete-modal-footer">
              <button
                className="delete-modal-btn-cancel"
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmChecked(false);
                  setError(null);
                }}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="delete-modal-btn-delete"
                onClick={handleDeleteAccount}
                disabled={!confirmChecked || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;

